/**
 * ConversationAutomationModal - Modal de visualização do Kanban de Automações para Conversas
 * Similar ao AutomationKanbanModal do CRM, mas para kanban_phases
 * 
 * RELEASE: 1.0.0 - 2026-01-26 - Modal inicial para Kanban de Conversas
 */

// v1.1.0 (2026-05-09):
//   - Removido SELECT direto a `kanban_phases`. As fases agora chegam por prop
//     a partir de Kanban.tsx (cache em memória de get_kanban_conversation_setup),
//     eliminando round-trip ao abrir o modal e zerando dependência REST direta.
import { useNavigate } from 'react-router-dom';
import { Zap, Loader2, LayoutGrid } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { AutomationKanbanColumn } from '@/components/crm/deals/AutomationKanbanColumn';
import { useConversationPhaseAutomations } from '@/hooks/useConversationPhaseAutomations';
import type { KanbanPhase } from '@/hooks/useKanban';

// =========================================================================
// Interface de props do componente
// =========================================================================
interface ConversationAutomationModalProps {
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback para alterar estado do modal */
  onOpenChange: (open: boolean) => void;
  /** ID da categoria do kanban */
  categoryId: string | null;
  /** Nome da categoria para exibição */
  categoryName: string;
  /**
   * Fases da categoria já carregadas pelo Kanban via RPC
   * `get_kanban_conversation_setup`. Evita SELECT direto a kanban_phases.
   */
  phases: KanbanPhase[];
}

/**
 * Modal que exibe o Kanban de Automações para Conversas
 * Reutiliza os componentes AutomationKanbanColumn e AutomationCard do CRM
 */
export function ConversationAutomationModal({
  open,
  onOpenChange,
  categoryId,
  categoryName,
  phases,
}: ConversationAutomationModalProps) {
  const navigate = useNavigate();


  // =========================================================================
  // Buscar automações vinculadas às fases (única fonte de dados remota)
  // =========================================================================
  const {
    getTriggerAutomations,
    getActionAutomations,
    totalAutomations,
    isLoading,
  } = useConversationPhaseAutomations(categoryId);

  // =========================================================================
  // Handlers para criar e editar fluxos
  // =========================================================================
  
  /**
   * Navega para criação de novo fluxo pré-configurado para a fase
   */
  const handleCreateFlow = (phaseId: string) => {
    // Passa preset_kanban_phase para o flow builder
    navigate(`/tenant/flows?new=true&preset_kanban_phase=${phaseId}&category=${categoryId}`);
    onOpenChange(false);
  };

  /**
   * Navega para edição de um fluxo existente
   */
  const handleEditFlow = (flowId: string) => {
    navigate(`/tenant/flows/${flowId}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[85vh] p-0 gap-0">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Zap className="h-5 w-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold">
                Regras de Automação e Gatilhos
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-0.5">
                <LayoutGrid className="h-3.5 w-3.5" />
                Categoria: {categoryName}
                {totalAutomations > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 text-[10px]">
                    {totalAutomations} {totalAutomations === 1 ? 'automação' : 'automações'}
                  </Badge>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Conteúdo com scroll horizontal */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {isLoading ? (
            // Estado de loading
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm">Carregando automações...</span>
              </div>
            </div>
          ) : phases.length === 0 ? (
            // Estado vazio - sem fases
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <LayoutGrid className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm">Nenhuma fase encontrada nesta categoria</p>
            </div>
          ) : (
            // Kanban de automações
            <ScrollArea className="h-full">
              <div className="flex gap-4 p-6 h-[calc(85vh-100px)]">
                {phases.map((phase) => (
                  <AutomationKanbanColumn
                    key={phase.id}
                    phaseId={phase.id}
                    phaseName={phase.name}
                    phaseColor={phase.color}
                    triggerAutomations={getTriggerAutomations(phase.id)}
                    actionAutomations={getActionAutomations(phase.id)}
                    onCreateFlow={handleCreateFlow}
                    onEditFlow={handleEditFlow}
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
